import type { BecVerifyConfig, UsbChoiceOption } from "../scenario/types";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";

/**
 * Episode 5 follow-up — the supplier-invoice BEC email is still sitting in
 * the inbox. This is the dedicated "how do you actually verify it" decision,
 * reached regardless of the verdict picked during triage, so every learner
 * gets the lesson: verify via a channel YOU look up, never contact info
 * pulled from the message itself. Mirrors {@link SignIn}/{@link MfaFatigue}.
 */
export function BecVerify({
  config,
  onEnded,
}: {
  config: BecVerifyConfig;
  onEnded: (key: string, choiceId: string, eventId?: string) => void;
}) {
  const pick = (opt: UsbChoiceOption) => {
    // 2026-07-25 feedback: the same correct/incorrect quiz chime every other
    // decision game uses (see PrivacySort.tsx / UsbDevice.tsx), not a generic
    // buzzer/click.
    if (opt.key === "risky") audio.chimeBad();
    else audio.chimeGood();
    onEnded(opt.key, opt.id, opt.eventId);
  };

  return (
    <div className="becverify">
      <div className="becverify-panel">
        <div className="becverify-ico" aria-hidden>
          {"\u{1F4B0}"}
        </div>
        <div className="becverify-headline">{config.headline ?? "Before you close up for the day…"}</div>
        <div className="becverify-card">
          <div className="becverify-subject">{config.subjectLine ?? "URGENT \u2014 updated bank details for Invoice #4471"}</div>
          <p className="becverify-excerpt">
            {config.bodyExcerpt ??
              "\u201CPlease action before 15:00 today \u2014 I'm in back-to-back meetings, so if anything's unclear just call me directly.\u201D"}
          </p>
          {config.signatureNumber && (
            <div className="becverify-sig">Signed off with a direct number: {config.signatureNumber}</div>
          )}
        </div>
        <ChoiceList prompt="How do you actually verify this before anything gets paid?">
          {config.options.map((opt) => (
            <ChoiceCard
              key={opt.id}
              category={opt.key === "risky" ? "comply" : "verify"}
              reply={opt.label}
              showRiskLabel={false}
              onClick={() => pick(opt)}
            />
          ))}
        </ChoiceList>
      </div>
    </div>
  );
}
